import { Directive, forwardRef, ElementRef, inject, input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Directive({
  selector: 'input[inputNumber]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputNumberDirective),
      multi: true
    }
  ],
  exportAs: 'inputNumber',
  host: {
    '(input)': 'onInput($event)',
    '(keypress)': 'onKeyPressEnter($event)',
    '(keydown)': 'onKeyDown($event)',
    '(paste)': 'onPaste($event)',
    '(blur)': 'onBlur()'
  }
})
export class InputNumberDirective implements ControlValueAccessor {
  private el: ElementRef<HTMLInputElement> = inject(ElementRef<HTMLInputElement>);

  allowedValues = input<string[] | undefined>(undefined, { alias: 'number-input' });

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  private writeValueUsed = false;

  public forceUpdate = false;

  private isValidNumber(value: string): boolean {
    // Allow:
    // - Negative numbers
    // - Numbers with decimal points
    // - Scientific notation (e.g., 1e-5)
    const pattern = /^-?\d*\.?\d+([eE][-+]?\d+)?$/;

    // Check if the value matches our pattern or is a valid number
    return !isNaN(value as any) && pattern.test(value);
  }

  private isPartialNumber(value: string): boolean {
    // Allow partial inputs that could become valid numbers:
    // - Empty string
    // - Just a minus sign
    // - Just a decimal point
    // - Minus followed by decimal point
    // - Numbers in progress (e.g., "1.", "1.2", "-0", etc.)
    // - Scientific notation in progress
    if (value === '' || value === '-' || value === '.' || value === '-.' || value === '0') {
      return true;
    }
    
    const partialPattern = /^-?\d*\.?\d*([eE][-+]?\d*)?$/;
    return partialPattern.test(value);
  }

  private normalizeInput(value: string): string {
    // Convert commas to dots
    let normalized = value.replace(/,/g, '.');
    
    // Handle minus signs more carefully to preserve scientific notation
    // Split by scientific notation parts to handle minus signs separately
    const scientificNotationMatch = normalized.match(/^([^eE]*)([eE][-+]?\d*)$/);
    
    if (scientificNotationMatch) {
      // We have scientific notation - handle mantissa and exponent separately
      const mantissa = scientificNotationMatch[1];
      const exponent = scientificNotationMatch[2];
      
      // Handle minus signs in mantissa only
      let normalizedMantissa = mantissa;
      if (normalizedMantissa.startsWith('-')) {
        normalizedMantissa = '-' + normalizedMantissa.substring(1).replace(/-/g, '');
      } else {
        normalizedMantissa = normalizedMantissa.replace(/-/g, '');
      }
      
      normalized = normalizedMantissa + exponent;
    } else {
      // No scientific notation - handle minus signs normally
      if (normalized.startsWith('-')) {
        normalized = '-' + normalized.substring(1).replace(/-/g, '');
      } else {
        normalized = normalized.replace(/-/g, '');
      }
    }
    
    // Handle multiple dots - keep only the first one
    const firstDotIndex = normalized.indexOf('.');
    if (firstDotIndex !== -1) {
      const beforeDot = normalized.substring(0, firstDotIndex + 1);
      const afterDot = normalized.substring(firstDotIndex + 1).replace(/\./g, '');
      normalized = beforeDot + afterDot;
    }
    
    // Remove leading zeros (except for "0", "0.", or "-0")
    if (normalized.match(/^-?0\d/)) {
      normalized = normalized.replace(/^(-?)0+(\d)/, '$1$2');
    }
    
    return normalized;
  }

  private emitValue(inputValue: string): void {
    // An incomplete value ('', '-', '.', '-.') has no numeric meaning yet —
    // emit `null` rather than coercing it to 0. Forcing a real 0 here (and in
    // `onBlur`, below) made every cleared number field "stick" at 0/the
    // control's min instead of letting the caller treat it as empty (SJD-38).
    if (inputValue === '' || inputValue === '-' || inputValue === '.' || inputValue === '-.') {
      this.onChange(null);
      return;
    }

    const parsed = parseFloat(inputValue);
    this.onChange(isNaN(parsed) ? null : parsed);
  }

  onInput(event: Event) {
    this.writeValueUsed = true;
    const target = event.target as HTMLInputElement;
    const cursorPosition = target.selectionStart || 0;
    
    // Normalize the input (convert commas, remove extra minus/dots, handle leading zeros)
    const normalizedValue = this.normalizeInput(target.value);
    
    // Check if it's a valid partial number
    if (this.isPartialNumber(normalizedValue)) {
      target.value = normalizedValue;
      // Restore cursor position if value changed
      if (normalizedValue !== target.value) {
        target.setSelectionRange(cursorPosition, cursorPosition);
      }
    } else {
      // Invalid input - only allow valid characters
      target.value = normalizedValue.replace(/[^0-9.eE\-]/g, '');
    }

    // Always emit a number value
    this.emitValue(target.value);
  }

  onKeyPressEnter(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;

    if (event.key === 'Enter' && this.isValidNumber(target.value)) {
      target.blur();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;

    // Handle arrow up/down to increment/decrement by step, unless the field cannot be changed
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !target.readOnly && !target.disabled) {
      event.preventDefault();
      
      // Get current value
      let currentValue = parseFloat(target.value) || 0;
      
      // Get step value (default to 1 if not specified)
      const step = target.step ? parseFloat(target.step) : 1;
      
      // Increment or decrement
      if (event.key === 'ArrowUp') {
        currentValue += step;
      } else {
        currentValue -= step;
      }
      
      // Apply min constraint
      if (target.min) {
        const minValue = parseFloat(target.min);
        if (currentValue < minValue) {
          currentValue = minValue;
        }
      }
      
      // Apply max constraint
      if (target.max) {
        const maxValue = parseFloat(target.max);
        if (currentValue > maxValue) {
          currentValue = maxValue;
        }
      }
      
      // Update the input value
      this.writeValueUsed = true;
      target.value = currentValue.toString();
      
      // Dispatch input event to trigger parent component's handler
      const inputEvent = new Event('input', { bubbles: true, cancelable: false });
      target.dispatchEvent(inputEvent);
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    
    const clipboardData = event.clipboardData?.getData('text');
    if (!clipboardData) {
      return false;
    }

    const target = event.target as HTMLInputElement;
    const normalizedValue = this.normalizeInput(clipboardData);
    
    // Check if it's a valid number or partial number
    if (this.isValidNumber(normalizedValue) || this.isPartialNumber(normalizedValue)) {
      // Get current selection
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      const currentValue = target.value;
      
      // Insert normalized value at cursor position
      const newValue = currentValue.substring(0, start) + normalizedValue + currentValue.substring(end);
      const finalValue = this.normalizeInput(newValue);
      
      if (this.isPartialNumber(finalValue)) {
        this.writeValueUsed = true;
        target.value = finalValue;
        
        // Set cursor position after pasted content
        const newCursorPos = start + normalizedValue.length;
        target.setSelectionRange(newCursorPos, newCursorPos);
        
        // Dispatch input event to trigger parent component's onInput handler
        const inputEvent = new Event('input', { bubbles: true, cancelable: false });
        target.dispatchEvent(inputEvent);
        
        // Mark as touched for form validation
        if (this.onTouched) {
          this.onTouched();
        }
      }
    }

    return false;
  }

  onBlur() {
    if (this.onTouched) {
      this.onTouched();
    }

    const rawValue = this.el.nativeElement.value;
    const outputValue = parseFloat(rawValue);

    // An incomplete value ('', '-', '.') is left exactly as the user left it —
    // forcing it to "0" here used to overwrite a field the instant it lost
    // focus, independent of whatever FormControl/validator state owns it
    // (SJD-38). The owning component decides what an empty field means
    // (invalid, revert to last value, etc.) via its own commit/blur handling.
    if (Number.isNaN(outputValue) || rawValue === '' || rawValue === '-' || rawValue === '.') {
      this.onChange(null);
      return;
    }

    let clamped = outputValue;
    this.el.nativeElement.value = clamped.toString();

    // Apply min constraint
    if (this.el.nativeElement.min) {
      const minValue = parseFloat(this.el.nativeElement.min.toString());
      if (clamped < minValue) {
        clamped = minValue;
        this.el.nativeElement.value = minValue.toString();
      }
    }

    // Apply max constraint
    if (this.el.nativeElement.max) {
      const maxValue = parseFloat(this.el.nativeElement.max.toString());
      if (clamped > maxValue) {
        clamped = maxValue;
        this.el.nativeElement.value = maxValue.toString();
      }
    }

    // Emit the final number value (explicitly as number type using + operator)
    this.onChange(+clamped);
  }

  // ControlValueAccessor interface methods
  writeValue(value: string | number | null): void {
    const displayValue = value !== null && value !== undefined ? String(value) : '';
    const current = this.el.nativeElement.value;

    // Text that already reads as the incoming number ('1.' or '1.50' for 1,
    // '-' or '' for null) is what the user is typing — keep it. Anything else
    // is a new value from code and replaces the text.
    const parsed = parseFloat(current);
    const showsSameValue = displayValue === ''
      ? Number.isNaN(parsed)
      : parsed === parseFloat(displayValue);

    if (!this.writeValueUsed || this.forceUpdate || !showsSameValue) {
      this.forceUpdate = false;
      this.el.nativeElement.value = displayValue;
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    // Wrap the onChange callback to ensure complete values are real numbers,
    // while passing `null` (incomplete/empty input) straight through.
    this.onChange = (value: number | null) => {
      fn(value === null ? null : +value);
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }
}
